/**
 * The M7 rail as one presentational panel, fed entirely by props.
 *
 * BrandAgentRail supplies the live chat; the dev preview supplies fixtures and
 * a scripted stream. Both render this, so what the preview shows is what ships.
 */
import {
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { ArrowUpRight, FileUp, History, PanelRightClose, SquarePen, X } from "lucide-react";
import type { ChatMessage, ChatModel, ChatSession } from "@/types/chat.type";
import type { ChatAttachmentItem } from "@/app/routes/dashboard/api/useSessionAttachments";
import type { StreamingBlock } from "@/app/routes/dashboard/components/streamingTurnBlocks";
import RailConversation, { type SendFlight } from "./RailConversation";
import RailComposer, { type RailComposerProps } from "./RailComposer";
import RailHistory from "./RailHistory";
import { placeAttachments, type AttachmentClaims } from "./railFormat";
import { useAgentRailStatusStore } from "../agentFocusStore";
import { useLiveLine } from "./useLiveLine";

const PUSH_MS = 320;

export type RailPanelProps = {
  /** A column on the desk, or a sheet on the phone. */
  mode: "column" | "sheet";
  /** The page the agent follows, as the tab names it ("Universe"). */
  followLabel: string;
  /** A finer place on that page, such as the slide being worked on. */
  followDetail?: string | null;
  sessions: ChatSession[];
  currentSessionId: string | null;
  onSelectSession: (sessionId: string) => void;
  onNewChat: () => void;
  onDeleteSession: (sessionId: string) => void;
  onRenameSession: (sessionId: string, title: string) => void;
  deletingSessionId: string | null;
  /** History and new chat wait while a chat loads, streams or is deleted. */
  historyDisabled: boolean;
  onClose: () => void;

  messages: ChatMessage[];
  isLoading: boolean;
  isDeleting: boolean;
  isStreaming: boolean;
  streamingBlocks: StreamingBlock[];
  streamError: string | null;
  attachments: ChatAttachmentItem[];
  /** Videos the agent attached from links, by document id. */
  linkedAttachments: Map<string, ChatAttachmentItem>;
  /** Files sent from this tab, by the message they went out with. */
  attachmentClaims?: AttachmentClaims;
  onRetryAttachment?: (documentId: string) => void;
  onRemoveAttachment?: (documentId: string) => void;
  onRespond: (reply: string) => void;
  respondDisabled: boolean;
  models: ChatModel[];

  suggestions: string[];
  onStarter: (suggestion: string) => void;
  startersDisabled: boolean;

  /** The composer's own props; the panel adds the files waiting to go out. */
  composer: Omit<
    RailComposerProps,
    "onSubmit" | "attachments" | "onRemoveAttachment" | "onRetryAttachment"
  > & { onSend: () => void };
  onDropFiles: (files: File[]) => void;
  dropDisabled: boolean;
  scrollRef: RefObject<HTMLDivElement | null>;
  /** The sheet's drag starts on its grabber and header. */
  onSheetDragStart?: (event: ReactPointerEvent<HTMLElement>) => void;
  /**
   * On a post page the rail's head is RailTabs, which carries the collapse;
   * the chat is then the Chat with M7 tab's panel, hidden while Comments is
   * open but kept mounted so the draft and the transcript survive.
   */
  tabPanel?: { id: string; labelledBy: string; hidden: boolean };
  /** A page's own question at the end of the chat, answered with no model call. */
  localTurn?: ReactNode;
};

function RailEmpty({
  suggestions,
  onStarter,
  disabled,
}: {
  suggestions: string[];
  onStarter: (suggestion: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="m7r-blank">
      <h2>What needs doing?</h2>
      <p>It can plan a month, draft a brief, or open the work you ask about.</p>
      <ul className="m7r-starters" aria-label="Starting points">
        {suggestions.map((suggestion) => (
          <li key={suggestion}>
            <button
              type="button"
              className="m7r-starter"
              disabled={disabled}
              onClick={() => onStarter(suggestion)}
            >
              <span>{suggestion}</span>
              <ArrowUpRight aria-hidden strokeWidth={1.75} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The whole rail takes a dropped file, not just the attach button. */
function useDropTarget(onDrop: (files: File[]) => void, disabled: boolean) {
  // dragenter/dragleave fire for every child crossed; a depth count keeps the
  // target from flickering off while the file moves across the rail.
  const depth = useRef(0);
  const [active, setActive] = useState(false);
  const hasFiles = (e: DragEvent) => e.dataTransfer.types.includes("Files");
  return {
    active,
    handlers: {
      onDragEnter: (e: DragEvent) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
        depth.current += 1;
        setActive(true);
      },
      onDragOver: (e: DragEvent) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      },
      onDragLeave: (e: DragEvent) => {
        if (disabled || !hasFiles(e)) return;
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setActive(false);
      },
      onDrop: (e: DragEvent) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        depth.current = 0;
        setActive(false);
        if (disabled) return;
        const files = Array.from(e.dataTransfer.files);
        if (files.length) onDrop(files);
      },
    },
  };
}

export function RailDropTarget() {
  return (
    <div className="m7r-drop" aria-hidden>
      <div className="m7r-drop-target">
        <FileUp aria-hidden strokeWidth={1.5} />
        <strong>Drop to attach</strong>
        <span>A PDF or a video</span>
        <small>PDF, MP4, MOV, WEBM, MKV</small>
      </div>
    </div>
  );
}

export default function RailPanel(props: RailPanelProps) {
  const {
    mode,
    followLabel,
    followDetail,
    sessions,
    currentSessionId,
    onSelectSession,
    onNewChat,
    onDeleteSession,
    onRenameSession,
    deletingSessionId,
    historyDisabled,
    onClose,
    composer,
    onDropFiles,
    dropDisabled,
    scrollRef,
    onSheetDragStart,
    tabPanel,
  } = props;
  const [view, setView] = useState<"chat" | "history">("chat");
  const [historyMounted, setHistoryMounted] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const flightRef = useRef<SendFlight | null>(null);
  const historyButton = useRef<HTMLButtonElement>(null);
  const drop = useDropTarget(onDropFiles, dropDisabled);

  // History stays mounted through its slide out, then leaves.
  useEffect(() => {
    if (view === "history" || !historyMounted) return;
    const id = window.setTimeout(() => setHistoryMounted(false), PUSH_MS);
    return () => window.clearTimeout(id);
  }, [view, historyMounted]);

  const openHistory = () => {
    setHistoryMounted(true);
    requestAnimationFrame(() => setView("history"));
  };
  const closeHistory = () => {
    setView("chat");
    requestAnimationFrame(() => historyButton.current?.focus({ preventScroll: true }));
  };

  const title = currentSessionId
    ? sessions.find((s) => s.id === currentSessionId)?.title.trim() || "Creo"
    : "Creo";
  // A page may publish one live line (Strategy: "Writing chapter 4 of 12.");
  // otherwise the head says what the chat is following.
  const pageLine = useAgentRailStatusStore((state) => state.line);
  const following = pageLine ?? `Following ${followLabel}${followDetail ? ` · ${followDetail}` : ""}`;
  const liveLine = useLiveLine(following);

  const { onSend, ...composerProps } = composer;
  const { staged } = placeAttachments(props.messages, props.attachments, props.attachmentClaims);
  const send = (from: { left: number; top: number } | null) => {
    if (from) flightRef.current = { ...from, text: composer.value, at: performance.now() };
    onSend();
  };

  const dragStart = (e: ReactPointerEvent<HTMLElement>) => {
    if (mode !== "sheet" || !onSheetDragStart) return;
    if ((e.target as HTMLElement).closest("button")) return;
    onSheetDragStart(e);
  };

  const emptyState: ReactNode = (
    <RailEmpty
      suggestions={props.suggestions}
      onStarter={props.onStarter}
      disabled={props.startersDisabled}
    />
  );

  return (
    <div
      className="m7r"
      data-view={view}
      data-mode={mode}
      {...(tabPanel
        ? {
            id: tabPanel.id,
            role: "tabpanel",
            "aria-labelledby": tabPanel.labelledBy,
            style: tabPanel.hidden ? { display: "none" } : undefined,
          }
        : {})}
      {...drop.handlers}
    >
      <div className="m7r-views">
        <section
          className="m7r-view m7r-view--chat"
          aria-label="Chat"
          inert={view === "history" || undefined}
        >
          <div className="m7r-grabber" onPointerDown={dragStart} aria-hidden />
          <div className="m7r-head" data-scrolled={scrolled || undefined} onPointerDown={dragStart}>
            <div className="m7r-head-lead">
              <button
                ref={historyButton}
                type="button"
                className="m7r-icon-btn"
                aria-label="Chat history"
                title="Chat history"
                disabled={historyDisabled}
                onClick={openHistory}
              >
                <History aria-hidden strokeWidth={1.75} />
              </button>
            </div>
            <div className="m7r-head-title">
              <strong>{title}</strong>
              <span aria-live="polite" key={liveLine.key} data-swap={liveLine.swapping || undefined} className="m7r-live-line">
                {liveLine.text}
              </span>
            </div>
            <div className="m7r-head-trail">
              <button
                type="button"
                className="m7r-icon-btn"
                aria-label="New chat"
                title="New chat"
                disabled={historyDisabled || currentSessionId === null}
                onClick={onNewChat}
              >
                <SquarePen aria-hidden strokeWidth={1.75} />
              </button>
              {tabPanel ? null : (
                <button
                  type="button"
                  className="m7r-icon-btn"
                  aria-label={mode === "sheet" ? "Close Creo" : "Collapse Creo"}
                  title={mode === "sheet" ? "Close" : "Collapse"}
                  onClick={onClose}
                >
                  {mode === "sheet" ? (
                    <X aria-hidden strokeWidth={1.75} />
                  ) : (
                    <PanelRightClose aria-hidden strokeWidth={1.75} />
                  )}
                </button>
              )}
            </div>
          </div>

          <RailConversation
            messages={props.messages}
            attachments={props.attachments}
            linkedAttachments={props.linkedAttachments}
            attachmentClaims={props.attachmentClaims}
            isLoading={props.isLoading}
            isDeleting={props.isDeleting}
            isStreaming={props.isStreaming}
            streamingBlocks={props.streamingBlocks}
            streamError={props.streamError}
            models={props.models}
            emptyState={emptyState}
            onRespond={props.onRespond}
            respondDisabled={props.respondDisabled}
            onRetryAttachment={props.onRetryAttachment}
            scrollRef={scrollRef}
            onScrolledChange={setScrolled}
            flightRef={flightRef}
            localTurn={props.localTurn}
          />

          <RailComposer
            {...composerProps}
            attachments={staged}
            onRemoveAttachment={props.onRemoveAttachment}
            onRetryAttachment={props.onRetryAttachment}
            onSubmit={send}
          />
        </section>

        {historyMounted ? (
          <section
            className="m7r-view m7r-view--history"
            aria-label="Chat history"
            // Still sliding away after Back: seen, but no longer reachable.
            inert={view !== "history" || undefined}
          >
            <div className="m7r-grabber" onPointerDown={dragStart} aria-hidden />
            <RailHistory
              mode={mode}
              sessions={sessions}
              currentSessionId={currentSessionId}
              deletingSessionId={deletingSessionId}
              disabled={historyDisabled}
              onBack={closeHistory}
              onClose={onClose}
              onRename={onRenameSession}
              onSelect={(id) => {
                onSelectSession(id);
                closeHistory();
              }}
              onNew={() => {
                onNewChat();
                closeHistory();
              }}
              onDelete={onDeleteSession}
            />
          </section>
        ) : null}
      </div>
      {drop.active ? <RailDropTarget /> : null}
    </div>
  );
}
